import { useEffect, useState } from "react";
import { motion } from "framer-motion";

// ScrollProgress: A thin progress bar at the top that indicates scroll position
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const update = () => {
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress((window.scrollY / total) * 100);
    };
    window.addEventListener("scroll", update);
    return () => window.removeEventListener("scroll", update);
  }, []);
  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-blue-200 z-50">
      <div
        className="h-full bg-blue-600 transition-all duration-200"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// SectionCard: Animated card wrapper for each section (h3 + content)
const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.03, y: -5 }}
    transition={{
      duration: 0.4,
      ease: "easeOut",
      type: "spring",
      stiffness: 200,
      damping: 20,
    }}
    viewport={{ once: true, amount: 0.2 }}
    className="p-6 bg-white/80 backdrop-blur-sm shadow-lg border border-[var(--color-secondary-muted)] rounded"
  >
    <h3 className="text-xl font-bold text-blue-900 mb-3">{title}</h3>
    {children}
  </motion.div>
);

// Paragraph: Styled paragraph component for consistent text appearance
const Paragraph = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <p
    className={`text-base text-gray-800 leading-relaxed text-justify ${className}`}
  >
    {children}
  </p>
);

const UcWavesRoadmap: React.FC = () => (
  <div className="relative overflow-hidden space-y-8 px-4 py-10 md:py-6 md:px-8">
    <ScrollProgress />

    {/* Introduction */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        ease: "easeOut",
        type: "spring",
        stiffness: 200,
        damping: 20,
      }}
      viewport={{ once: true }}
      className="max-w-8xl mx-auto text-center space-y-4"
    >
      <h2 className="text-3xl font-bold text-primary-dark">
        UcWaves Roadmap: Phased Growth & Feature Rollout
      </h2>
      <Paragraph className="text-gray-700 max-w-6xl mx-auto">
        Following the launch of UcWaves, our teams will follow a clear, phased
        roadmap to scale functionality, deepen decentralization, and deliver
        advanced DeFi features. Each stage leverages proven technologies and
        industry-leading bridges (Stargate, LayerZero, Wormhole) to ensure
        reliability, scalability, and trust.
      </Paragraph>
    </motion.div>

    {/* 2-Column Layout */}
    <div className="flex flex-col md:flex-row gap-6 max-w-6xl mx-auto">
      <div className="flex-1 flex flex-col space-y-6">
        <SectionCard title="Phase 1: dApp Deployment & IPFS Migration">
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              <strong>Web Launch:</strong> Deploy UcWaves dApp on a resilient
              hosting solution, integrating wallet connections (EOA and
              ERC-4337), swaps, staking, and governance.
            </li>
            <br />
            <li>
              <strong>IPFS Transition:</strong> Publish front-end assets to
              IPFS/Arweave, anchored via ENS and a gateway, for immutable,
              censorship-resistant delivery.
            </li>
          </ul>
          <Paragraph>
            <strong>Outcome:</strong> A decentralized UI backbone, eliminating
            single points of failure.
          </Paragraph>
        </SectionCard>

        <SectionCard title="Phase 2: Staking & Liquidity Rewards Activation">
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              <strong>Activate Staking:</strong> Fund and set the three staking
              contracts (NAU, NAUY, NAUN) for community participation.
            </li>
            <br />
            <li>
              <strong>Liquidity Mining:</strong> Begin LP reward emissions on
              Uniswap V3 pools to foster deep, reliable market liquidity.
            </li>
          </ul>
          <Paragraph>
            <strong>Outcome:</strong> Initial token lock-up and incentivized
            liquidity provisioning to kickstart network effects.
          </Paragraph>
        </SectionCard>

        <SectionCard title="Phase 3: Ownership Renunciation & DAO Transition">
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              <strong>Renounce Ownership:</strong> The Deployer Admin wallet
              relinquishes control of core contracts, solidifying immutable
              protocol logic.
            </li>
            <br />
            <li>
              <strong>DAO Handover:</strong> Transfer governance and
              staking-admin roles to an on-chain DAO, empowering token holders
              to propose upgrades and parameter changes.
            </li>
          </ul>
          <Paragraph>
            <strong>Outcome:</strong> Fully decentralized governance and
            increased community stewardship.
          </Paragraph>
        </SectionCard>


      </div>

      <div className="flex-1 flex flex-col space-y-6">
        <SectionCard title="Phase 4: Cross-Chain Liquidity & Staking Pools">
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              <strong>Bridging Integration:</strong> Utilize Stargate,
              LayerZero, and Wormhole to connect liquidity and staking positions
              across multiple L2s.
            </li>
            <br />
            <li>
              <strong>Cross-Chain Staking:</strong> Deploy cross-chain staking
              contracts enabling users to stake on one network and earn rewards
              on another with minimal friction.
            </li>
          </ul>
          <Paragraph>
            <strong>Outcome:</strong> Unified liquidity and yield opportunities
            spanning multiple chains without siloed ecosystems.
          </Paragraph>
        </SectionCard>

        <SectionCard title="Phase 5: AI-Driven Sentiment Analysis & Community Growth">
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              <strong>Sentiment Intelligence:</strong> Incorporate NLP models to
              analyze on-chain governance discussion and off-chain social media,
              quantifying sentiment trends per topic.
            </li>
            <br />
            <li>
              <strong>Data-Driven Tokenomics:</strong> Feed sentiment metrics
              into reward algorithms, aligning incentives with community moods.
            </li>
            <li>
              <strong>Grants & Partnerships:</strong> Launch developer grants,
              hackathons, and protocol integrations to accelerate ecosystem
              growth and innovation.
            </li>
          </ul>
          <Paragraph>
            <strong>Outcome:</strong> Smarter governance, incentivized
            participation, and a thriving developer community.
          </Paragraph>
        </SectionCard>

        <SectionCard title="Phase 6: Lending & Borrowing with Opinion Tokens">
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              <strong>Credit Markets:</strong> Introduce lending/borrowing
              platforms where NAUY and NAUN function as collateral, expanding
              token utility.
            </li>
            <br />
            <li>
              <strong>Risk Controls:</strong> Leverage on-chain oracles and
              automated liquidation engines to maintain safe collateralization
              ratios.
            </li>
          </ul>
          <Paragraph>
            <strong>Outcome:</strong> Advanced DeFi primitives that deepen
            opinion-token utility and unlock new financial use cases.
          </Paragraph>
        </SectionCard>
      </div>
    </div>

    <Paragraph className="text-gray-700 max-w-6xl mx-auto text-justify">
      This roadmap balances rapid feature rollout with robust decentralization
      and scalable architecture. By methodically integrating multi-chain
      support, cutting-edge bridges (Stargate, LayerZero, Wormhole), AI
      insights, and lending markets, UcWaves will transform into a versatile,
      community-driven platform for uncensored opinion-sharing across a spectrum
      of global topics.
    </Paragraph>
  </div>
);

export default UcWavesRoadmap;
